import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp as amplifyConfirmSignUp, AuthError } from 'aws-amplify/auth';
import PianoBackground from '../../assets/images/pianoBackground.png';
import Logo from 'Components/Logo';
import { CustomCircularProgress } from 'Components/StyledComponents';
import CheckIcon from '@mui/icons-material/Check';
import { handleAuthErrors } from 'Utils/Forms';
import * as authActions from 'Actions/auth';
import * as eventActions from 'Actions/events';
import { AppDispatch } from 'Reducers';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';

const Verification: React.FC = () => {
  const [verified, setVerified] = React.useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = React.useState(false);
  const [error, setError] = React.useState('');
  const verificationStarted = React.useRef(false);
  const auth = useAuthWrapperContext();

  const navigate = useNavigate();
  const search = useLocation().search.slice(1);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (autoLoginAttempted && auth.isAuthenticated) {
      navigate('/welcome');
    } else if (auth.isAuthenticated && !autoLoginAttempted) {
      navigate('/roadmap');
    }
  }, [auth.isAuthenticated, autoLoginAttempted, navigate]);

  useEffect(() => {
    if (verificationStarted.current) {
      return;
    }

    const query = new URLSearchParams(search);
    const params: { [key: string]: string } = {};
    for (const [key, value] of query.entries()) {
      params[key] = value;
    }

    if (params['user_name'] && params['confirmation_code'] && params['email']) {
      verificationStarted.current = true;
      amplifyConfirmSignUp({
        username: params['user_name'],
        confirmationCode: params['confirmation_code'],
      })
        .then(() => {
          setVerified(true);
          dispatch(eventActions.accountVerifiedAction());
          const storedPass =
            auth.tempSignUpPass || localStorage.getItem('tempSignUpPass') || undefined;
          if (storedPass && params['email']) {
            const email = params['email'].replace(' ', '+');
            setAutoLoginAttempted(true);
            localStorage.removeItem('tempSignUpPass');
            auth.logInUsername(email, storedPass, '');
          } else {
            localStorage.removeItem('tempSignUpPass');
            navigate('/signin');
          }
        })
        .catch((catchError: Error) => {
          verificationStarted.current = false;
          const authError = catchError as unknown as AuthError;
          const err = handleAuthErrors(authError);
          dispatch(authActions.setTempSignUpPass(undefined));
          if (err?.name) setError(err.name);
          if (err?.unknown) setError(err.unknown);
        });
    } else {
      navigate('/signin');
    }
  }, [auth.tempSignUpPass, auth, dispatch, navigate, search]);

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        style={{
          height: '100vh',
          opacity: 0.3,
          position: 'absolute',
        }}
        src={PianoBackground}
        alt="piano-background"
      ></img>
      <Box className="page-div-styles">
        <Box className="filter-div-styles">
          <Box className="sign-in-div-styles">
            <Logo />
            <Box className="desc-container welcome-container">
              <Typography variant="h3" className="title-welcome" style={{ marginTop: '50px' }}>
                {!verified && 'Verification Pending'}
                {verified && 'Success'}
              </Typography>
              {((!verified && !error) || auth.processPending) && <CustomCircularProgress />}
              {verified && !error && !auth.processPending && (
                <CheckIcon color="success" sx={{ height: '80px', width: '80px' }} />
              )}
              {error && <Typography color="red">{error}</Typography>}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Verification;
